import React, { useEffect, useMemo, useState } from "react";
import {
    getSupplierOrgRoutingRules,
    getSupplierOrgRoutingOptions,
    createSupplierOrgRoutingRule,
    updateSupplierOrgRoutingRule,
    deleteSupplierOrgRoutingRule,
} from "../api/supplierOrgRoutingApi";

const SCOPE_LABELS = { store: "Магазин", enterprise: "Предприятие", global: "Глобально" };

const emptyDraft = {
    scope_type: "enterprise",
    store_id: "",
    enterprise_code: "",
    supplier_code: "",
    business_organization_id: "",
    notes: "",
};

const formatApiError = (error, fallback) =>
    error?.response?.data?.detail || error?.message || fallback;

const SupplierOrgRoutingPage = () => {
    const [rules, setRules] = useState([]);
    const [options, setOptions] = useState({ organizations: [], suppliers: [], stores: [], enterprises: [] });
    const [draft, setDraft] = useState(emptyDraft);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const loadAll = async () => {
        setLoading(true);
        setError("");
        try {
            const [rulesData, optionsData] = await Promise.all([
                getSupplierOrgRoutingRules(),
                getSupplierOrgRoutingOptions(),
            ]);
            setRules(rulesData);
            setOptions(optionsData);
        } catch (err) {
            setError(formatApiError(err, "Не удалось загрузить правила маршрутизации."));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAll();
    }, []);

    const orgById = useMemo(() => {
        const map = {};
        (options.organizations || []).forEach((o) => { map[String(o.id)] = o; });
        return map;
    }, [options.organizations]);

    const onDraftChange = (field, value) => {
        setDraft((prev) => {
            const next = { ...prev, [field]: value };
            if (field === "scope_type") {
                next.store_id = "";
                next.enterprise_code = "";
            }
            return next;
        });
    };

    const handleCreate = async () => {
        setError("");
        setSuccess("");
        if (!draft.supplier_code) return setError("Выберите поставщика.");
        if (!draft.business_organization_id) return setError("Выберите целевое юрлицо.");
        if (draft.scope_type === "store" && !draft.store_id) return setError("Выберите магазин для scope=Магазин.");
        if (draft.scope_type === "enterprise" && !draft.enterprise_code) return setError("Выберите предприятие для scope=Предприятие.");

        const payload = {
            scope_type: draft.scope_type,
            supplier_code: draft.supplier_code,
            business_organization_id: Number(draft.business_organization_id),
            notes: draft.notes || null,
            store_id: draft.scope_type === "store" ? Number(draft.store_id) : null,
            enterprise_code: draft.scope_type === "enterprise" ? draft.enterprise_code : null,
        };
        setSaving(true);
        try {
            await createSupplierOrgRoutingRule(payload);
            setSuccess("Правило добавлено.");
            setDraft((prev) => ({ ...emptyDraft, scope_type: prev.scope_type }));
            await loadAll();
        } catch (err) {
            setError(formatApiError(err, "Не удалось добавить правило."));
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (rule) => {
        try {
            await updateSupplierOrgRoutingRule(rule.id, { is_active: !rule.is_active });
            await loadAll();
        } catch (err) {
            setError(formatApiError(err, "Не удалось изменить статус правила."));
        }
    };

    const handleChangeOrg = async (rule, newOrgId) => {
        try {
            await updateSupplierOrgRoutingRule(rule.id, { business_organization_id: Number(newOrgId) });
            await loadAll();
        } catch (err) {
            setError(formatApiError(err, "Не удалось изменить юрлицо правила."));
        }
    };

    const handleDelete = async (rule) => {
        if (!window.confirm(`Удалить правило: ${rule.supplier_code} → ${rule.organization_name || rule.business_organization_id}?`)) return;
        try {
            await deleteSupplierOrgRoutingRule(rule.id);
            await loadAll();
        } catch (err) {
            setError(formatApiError(err, "Не удалось удалить правило."));
        }
    };

    const scopeRefLabel = (rule) => {
        if (rule.scope_type === "store") return rule.store_name || rule.store_code || `store#${rule.store_id}`;
        if (rule.scope_type === "enterprise") return rule.enterprise_code || "—";
        return "все контуры";
    };

    return (
        <div className="og-detail">
            <div className="og-callout">
                <span className="ic">ⓘ</span>
                <div>
                    Переопределяет <b>organizationId</b> исходящего SalesDrive-заказа для конкретного поставщика:
                    заказ, ТТН и NP-кабинет пойдут на указанное юрлице. По умолчанию (нет правила) — юрлице магазина.
                    Приоритет: <b>Магазин → Предприятие → Глобально</b>.
                    <br />
                    Смешанный заказ (позиции от разных поставщиков): если все ведут в одно юрлице — берётся оно;
                    если расходятся — остаётся юрлице магазина. <b>WMS-фулфилмент не затрагивается.</b>
                </div>
            </div>

            {error && (
                <div className="og-callout danger">
                    <span className="ic">⚠</span>
                    <div>{error}</div>
                </div>
            )}
            {success && (
                <div className="og-callout good">
                    <span className="ic">✓</span>
                    <div>{success}</div>
                </div>
            )}

            <div className="og-block">
                <div className="og-bh">
                    <span className="ic">＋</span>
                    <h4>Новое правило</h4>
                </div>
                <div className="og-grid3">
                    <div className="og-fld">
                        <label>Scope</label>
                        <select className="og-inp" value={draft.scope_type} onChange={(e) => onDraftChange("scope_type", e.target.value)}>
                            <option value="store">Магазин</option>
                            <option value="enterprise">Предприятие</option>
                            <option value="global">Глобально</option>
                        </select>
                    </div>

                    {draft.scope_type === "store" && (
                        <div className="og-fld">
                            <label>Магазин</label>
                            <select className="og-inp" value={draft.store_id} onChange={(e) => onDraftChange("store_id", e.target.value)}>
                                <option value="">— выберите —</option>
                                {(options.stores || []).map((s) => (
                                    <option key={s.id} value={s.id}>
                                        {s.store_name || s.store_code} ({s.store_code})
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {draft.scope_type === "enterprise" && (
                        <div className="og-fld">
                            <label>Предприятие</label>
                            <select className="og-inp" value={draft.enterprise_code} onChange={(e) => onDraftChange("enterprise_code", e.target.value)}>
                                <option value="">— выберите —</option>
                                {(options.enterprises || []).map((ent) => (
                                    <option key={ent.enterprise_code} value={ent.enterprise_code}>
                                        {ent.enterprise_name || ent.enterprise_code} ({ent.enterprise_code})
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="og-fld">
                        <label>Поставщик</label>
                        <select className="og-inp" value={draft.supplier_code} onChange={(e) => onDraftChange("supplier_code", e.target.value)}>
                            <option value="">— выберите —</option>
                            {(options.suppliers || []).map((sup) => (
                                <option key={sup.code} value={sup.code}>
                                    {sup.name || sup.code} ({sup.code})
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="og-fld">
                        <label>Целевое юрлице</label>
                        <select className="og-inp" value={draft.business_organization_id} onChange={(e) => onDraftChange("business_organization_id", e.target.value)}>
                            <option value="">— выберите —</option>
                            {(options.organizations || []).map((org) => (
                                <option key={org.id} value={org.id}>
                                    {org.short_name}{org.salesdrive_organization_id ? ` / SalesDrive ${org.salesdrive_organization_id}` : ""}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="og-fld og-col-span">
                        <label>Заметка</label>
                        <input className="og-inp" value={draft.notes} onChange={(e) => onDraftChange("notes", e.target.value)} placeholder="необязательно" />
                    </div>
                </div>
                <div className="og-save-bar notop">
                    <button type="button" className="og-btn og-btn-primary" disabled={saving} onClick={handleCreate}>
                        {saving ? "Сохранение…" : "Добавить правило"}
                    </button>
                </div>
            </div>

            <div className="og-block">
                <div className="og-bh">
                    <span className="ic">☰</span>
                    <h4>Правила {loading ? "…" : `(${rules.length})`}</h4>
                </div>
                <div className="og-tbl-wrap">
                    <table className="og-tbl">
                        <thead>
                            <tr>
                                <th>Scope</th>
                                <th>Где</th>
                                <th>Поставщик</th>
                                <th>Целевое юрлице</th>
                                <th>Активно</th>
                                <th>Заметка</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rules.length === 0 && !loading && (
                                <tr><td colSpan={7}>Правил пока нет — по умолчанию все поставщики идут на юрлице магазина.</td></tr>
                            )}
                            {rules.map((rule) => (
                                <tr key={rule.id}>
                                    <td><span className="og-pill">{SCOPE_LABELS[rule.scope_type] || rule.scope_type}</span></td>
                                    <td>{scopeRefLabel(rule)}</td>
                                    <td>
                                        <b>{rule.supplier_code}</b>
                                        {rule.supplier_name ? <div className="og-mini">{rule.supplier_name}</div> : null}
                                    </td>
                                    <td>
                                        <select
                                            className="og-inp"
                                            value={String(rule.business_organization_id)}
                                            onChange={(e) => handleChangeOrg(rule, e.target.value)}
                                        >
                                            {(options.organizations || []).map((org) => (
                                                <option key={org.id} value={org.id}>{org.short_name}</option>
                                            ))}
                                        </select>
                                        <div className="og-mini">
                                            SalesDrive: {rule.salesdrive_organization_id
                                                || orgById[String(rule.business_organization_id)]?.salesdrive_organization_id
                                                || "— не задан —"}
                                        </div>
                                    </td>
                                    <td>
                                        <button
                                            type="button"
                                            onClick={() => handleToggleActive(rule)}
                                            className={`og-badge og-btn ${rule.is_active ? "on" : "off"}`}
                                        >
                                            {rule.is_active ? "активно" : "выключено"}
                                        </button>
                                    </td>
                                    <td>{rule.notes || ""}</td>
                                    <td>
                                        <button
                                            type="button"
                                            onClick={() => handleDelete(rule)}
                                            className="og-btn og-btn-soft-danger og-btn-sm"
                                        >
                                            удалить
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default SupplierOrgRoutingPage;
